import React, { Component } from "react";

import { withRouter } from "react-router-dom";
import { compose } from "recompose";
import { withStyles } from "@material-ui/core/styles";
import { withAuthorization } from "../Session";

import { Button, CircularProgress, Typography } from "@material-ui/core";
import { httpsCallable } from "@firebase/functions";
import Stepper from "./Stepper";
import Actions from "./RepActions";
import Start from "./steps/Start";
import Business from "./steps/Business";
import Principal from "./steps/Principal";
import Account from "./steps/Account";
import Financial from "./steps/Financial";
import { mask } from "../../util/MaskUtil";
import { reconcileLegacyMerchantEdit, applyTaxIdChoice, joinPrincipalName, CURRENCY_FIELDS, currencyInput, SOLE_PROPRIETOR, TAX_ID_PLACEHOLDER } from "../../util/ApplicationFields";
import { validateStep } from "../../util/ValidationUtil";
import Cart from "./steps/Cart";
import NewApplicationStep from "./application-steps";
import MerchantShell from "./application-steps/Shell";
import { StepHeader } from "./application-steps/ui";
import { changeMerchantField, validateMerchantStep, CONFIRM_SSN } from "./application-steps/values";
import { USE_NEW_REP_FLOW } from "./flow";
import ApplicationLocaleProvider from "../../i18n/ApplicationLocaleProvider";
import { DEFAULT_LOCALE } from "../../i18n/locale";

import { withFirebase } from "../Firebase";

import IpAddressApi from "../../api/IpAddress";

import styles from "./styles";
import {
  addDoc,
  onSnapshot,
  setDoc,
  deleteField,
  doc,
  collection
} from "@firebase/firestore";

const REP_STEP_LABELS = ["Get Started", "Business", "Owner", "Sales", "Banking", "Payment", "Send"];

export class Application extends Component {
  constructor(props) {
    super(props);
    let application = {
      repStarted: true,
      activeStep: 0,
      status: "Incomplete",
      shippingDifferent: false,
      residenceDifferent: false,
    };

    this.state = {
      activeStep: 0,
      loading: false,
      savingNewApplication: false,
      promptResume: null,
      user: null,
      application,
      transaction: undefined,
      linkId: props.linkId,
      paymentLoading: false,
      errors: {}
    };
  }

  usesNewFlow = () => USE_NEW_REP_FLOW;

  componentDidMount = async () => {
    const { linkId } = this.props;
    const firestore = this.props.firebase.firestore;
    if (linkId) {
      onSnapshot(doc(firestore, "links", linkId), snapshot => {
        if (snapshot.exists) {
          const data = snapshot.data();
          this.setState({
            application: {
              ...this.state.application,
              agent: 0, //parseInt(data.agent.irisUserId),
              link: { ...data, id: snapshot.id },
              uid: data.uid,
              processor: data.processor || "paysafe"
            }
          });
        }
      });
    }
  };

  handleResetSsn = () => {
    const application = { ...this.state.application };
    application.encryptedSocialSecurityNumber = "";
    application.socialSecurityNumber = "";
    application[CONFIRM_SSN] = "";
    this.setState({ application });
  };

  handlePaymentLoading = paymentLoading => this.setState({ paymentLoading });

  // Like the merchant flow, only earlier steps open from the progress bar.
  handleStepClick = step => {
    if (this.state.loading || step >= this.state.activeStep) return;
    this.setState({ activeStep: step, errors: {}, paymentLoading: false });
  };

  createApplication = async application => {
    this.setState({ savingNewApplication: true, loading: true });

    const ipAddressResults = await IpAddressApi.getIpAddress();

    const newApplication = {
      ...application,
      activeStep: 1,
      createdAt: new Date(),
      repIpAddress: ipAddressResults.ipAddress
    };

    const firestore = this.props.firebase.firestore;
    const d = await addDoc(
      collection(firestore, "repApplications"),
      newApplication
    );

    this.setState({
      savingNewApplication: false,
      loading: false,
      application: {
        ...newApplication,
        id: d.id
      }
    });
    return d.id;
  };

  saveApplication = async application => {
    const firestore = this.props.firebase.firestore;
    return await setDoc(
      doc(firestore, "repApplications", application.id),
      { ...application, socialSecurityNumber: deleteField(), [CONFIRM_SSN]: deleteField() },
      { merge: true }
    );
  };

  handleNext = async () => {
    if (this.isValid()) {
      const steps = this.getSteps();
      if (this.state.activeStep === steps.length - 1) {
        this.handleSubmit();
      } else {
        let nextStep = this.state.activeStep + 1;
        let application = { ...this.state.application };
        if (nextStep > application.activeStep) {
          application.activeStep = nextStep;
        }
        if (this.state.activeStep === 0) {
          const id = await this.createApplication(application);
          application.id = id;
        } else {
          if (nextStep === 2) {
            if (this.usesNewFlow() && application.ownershipType === SOLE_PROPRIETOR) {
              application.legalBusinessName = joinPrincipalName(application);
            }
            application.dateBusinessStarted = "01/01/" + application.businessYear;
            if (!application.homePhone) {
              application.homePhone = application.businessPhone;
            }
            applyTaxIdChoice(application);

            if (!application.mailingDifferent) {
              application.mailingAddress1 = application.businessAddress1;
              if (application.businessAddress2) {
                application.mailingAddress2 = application.businessAddress2;
              }
              application.mailingCity = application.businessCity;
              application.mailingState = application.businessState;
              application.mailingZip = application.businessZip;
            }
            if (!application.shippingDifferent) {
              application.shippingAddress1 = application.businessAddress1;
              if (application.businessAddress2) {
                application.shippingAddress2 = application.businessAddress2;
              }
              application.shippingCity = application.businessCity;
              application.shippingState = application.businessState;
              application.shippingZip = application.businessZip;
            }
            if (this.usesNewFlow() && !application.businessAddress2) {
              if (!application.mailingDifferent) application.mailingAddress2 = "";
              if (!application.shippingDifferent) application.shippingAddress2 = "";
            }
          }
          if (nextStep === 3) {
            const socialSecurityNumber = application.socialSecurityNumber;

            if (socialSecurityNumber) {
            try {
              const {
                data: { encryptedText, salt }
              } = await httpsCallable(
                this.props.firebase.functions,
                "encryptField"
              )({
                plainText: socialSecurityNumber
              });
              if (!encryptedText || !salt) throw new Error("SSN encryption failed");
              application.encryptedSocialSecurityNumber = encryptedText;
              application.encryptedSocialSecurityNumberSalt = salt;
            } catch (err) {
              this.setState({ errors: {
                ...this.state.errors,
                socialSecurityNumber: "We could not securely save your SSN. Please try again."
              } });
              return;
            }
            delete application.socialSecurityNumber;
            delete application[CONFIRM_SSN];
            }

            if (!application.residenceDifferent) {
              application.homeAddress1 = application.businessAddress1;
              application.homeAddress2 = application.businessAddress2 || "";
              application.homeCity = application.businessCity;
              application.homeState = application.businessState;
              application.homeZip = application.businessZip;
            }
            applyTaxIdChoice(application);
          }
          if (nextStep === 4) {
            application.monthlyAmexVolume = Math.round(application.cardSales * 0.1);
            application.cardSalesYear = Math.round(application.cardSales * 12);
          }
          if (nextStep === 6) {
            if (application.link && application.link.paymentType === "ACH") {
              application.paymentBankName = application.bankName;
              application.paymentAccountNumber = application.bankAccountNumber;
              application.paymentRoutingNumber = application.routingNumber;
            }
            if (application.paymentType === "Free") {
              nextStep += 1;
            }
          }
          await this.saveApplication(application);
        }
        this.setState({ application, activeStep: nextStep });
      }
    }
    window.scrollTo(0, 0);
  };

  handleBack = () => {
    const previousStep = this.state.activeStep - 1;
    if (this.state.activeStep > 0) {
      this.setState({ activeStep: previousStep, errors: {}, paymentLoading: false });
    }
  };

  handleReset = () => {
    this.setState({ activeStep: 0 });
  };

  handleSaveAndSend = async () => {
    try {
        const { application } = this.state;
        await this.saveApplication(application);
        await httpsCallable(
            this.props.firebase.functions,
            "sendRepApplication"
        )({
          data: {
            repApplicationId: application.id
        }});
        this.setState({ sent: true });
        this.props.onSave && this.props.onSave(application.id);
    } catch (error) {
        console.error("Error saving and sending application:", error);
    }
};


  handleSubmit = async () => {
    this.setState({ loading: true });
  };

  handleApplicationTextChange = name => event => {
    if (this.usesNewFlow()) {
      const value = event.target.value;
      this.setState(state => ({
        application: changeMerchantField(state.application, name, value)
      }));
      return;
    }
    const application = { ...this.state.application };
    application[name] = mask(name, event.target.value);
    if (CURRENCY_FIELDS.includes(name)) {
      const raw = currencyInput(event.target.value);
      if (raw === null) return;
      application[name] = raw;
    }
    if (
      name === "ownershipType" &&
      event.target.value !== SOLE_PROPRIETOR
    ) {
      application.soleProprietorHasEin = "";
      if (application.federalTaxId === TAX_ID_PLACEHOLDER) application.federalTaxId = "";
    }
    reconcileLegacyMerchantEdit(application, name);
    this.setState({ application });
  };

  handleCheckChange = name => event => {
    let application = { ...this.state.application };
    application[name] = event.target.checked;
    if (name === "shippingDifferent" && event.target.checked) {
      application.shippingAddress1 = "";
      application.shippingAddress2 = "";
      application.shippingCity = "";
      application.shippingState = "";
      application.shippingZip = "";
    }
    if (name === "residenceDifferent" && event.target.checked) {
      application.homeAddress1 = "";
      application.homeAddress2 = "";
      application.homeCity = "";
      application.homeState = "";
      application.homeZip = "";
    }
    this.setState({ application });
  };

  handlePlanChange = event => {
    let application = { ...this.state.application };
    application.plan = event.target.value;
    this.setState({ application });
  };

  setApplicationPricing = pricing => {
    let application = { ...this.state.application };
    application.pricing = pricing;
    this.setState({ application });
  };

  getSteps = () => {
    return [
      "Email Address",
      "Business Info",
      "Principal Info",
      "Account Info",
      "Financial Info",
      "Payment Info",
      "Done"
    ];
  };

  getStepContent = (stepIndex, application, errors) => {
    switch (stepIndex) {
      case 0:
        return (
          <Start
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            errors={errors}
          />
        );
      case 1:
        return (
          <Business
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            onCheckChange={this.handleCheckChange}
            errors={errors}
          />
        );
      case 2:
        return (
          <Principal
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            onCheckChange={this.handleCheckChange}
            errors={errors}
            isSsnEncrypted={
              application.encryptedSocialSecurityNumber
            }
            resetSsn={this.handleResetSsn}
          />
        );
      case 3:
        return (
          <Account
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            errors={errors}
          />
        );
      case 4:
        return (
          <Financial
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            errors={errors}
          />
        );
      case 5:
        return (
          <Cart
            application={application}
            onApplicationTextChange={this.handleApplicationTextChange}
            setApplicationPricing={this.setApplicationPricing}
            onCheckChange={this.handleCheckChange}
            onBack={this.handleBack}
            onNext={this.handleNext}
            errors={errors}
            transaction={this.state.transaction}
          />
        );
      case 6:
        return (
          <div className={this.props.classes.done}>
            {this.state.sent ? (
              <Typography>Sent.</Typography>
            ) : (
              <Typography>
                Click <b>SAVE & SEND</b> to send an email to the customer.
              </Typography>
            )}
          </div>
        );
      default:
        throw RangeError("Invalid step");
    }
  };

  isValid = () => {
    const { activeStep, application } = this.state;
    // The rep and merchant steps share the same rules; Done has none.
    const validationResult =
      this.usesNewFlow() && activeStep < 6
        ? validateMerchantStep(activeStep, application)
        : validateStep(activeStep, application);
    if (!validationResult.isValid) {
      this.setState({ errors: validationResult.errors });
    } else {
      this.setState({ errors: {} });
    }
    return validationResult.isValid;
  };

  renderNewFlow() {
    const { activeStep, application, loading, paymentLoading, errors, sent } = this.state;
    const last = activeStep === this.getSteps().length - 1;
    const actions = (
      <nav className="merchant-actions merchant-actions-rep" aria-label="Application navigation">
        <Button variant="contained" disabled={activeStep === 0 || loading} onClick={this.handleBack}>
          Back
        </Button>
        <Button variant="outlined" color="primary" disabled={activeStep === 0 || loading} onClick={this.handleSaveAndSend}>
          Save & Send
        </Button>
        <Button variant="contained" color="secondary" disabled={last || loading || paymentLoading} onClick={this.handleNext}>
          {loading ? <CircularProgress size={24} color="inherit" aria-label="Saving" /> : "Next"}
        </Button>
      </nav>
    );
    return (
      <ApplicationLocaleProvider locale={DEFAULT_LOCALE}>
        <MerchantShell embedded application={application} activeStep={activeStep}
          onStepClick={this.handleStepClick} loading={loading} wide={activeStep === 1}
          actions={actions} stepLabels={REP_STEP_LABELS}>
          {last ? (
            <StepHeader title="Send to Customer">
              {sent
                ? "Sent. The customer received an email with the application link."
                : "Click Save & Send to email the application link to the customer. They review it, sign and submit."}
            </StepHeader>
          ) : (
            <NewApplicationStep key={activeStep} step={activeStep} application={application}
              errors={errors} onApplicationTextChange={this.handleApplicationTextChange}
              onCheckChange={this.handleCheckChange} resetSsn={this.handleResetSsn}
              setApplicationPricing={this.setApplicationPricing}
              onPaymentLoading={this.handlePaymentLoading} />
          )}
        </MerchantShell>
      </ApplicationLocaleProvider>
    );
  }

  render() {
    if (this.usesNewFlow()) return this.renderNewFlow();
    const { classes } = this.props;
    const { activeStep, application, loading, errors } = this.state;
    const steps = this.getSteps();
    const stepIndex = activeStep;

    return (
      <div className={classes.root}>
        <Stepper steps={steps} activeStep={stepIndex} />
        {this.getStepContent(activeStep, application, errors)}
        <Actions
          application={application}
          steps={steps}
          activeStep={activeStep}
          onNext={this.handleNext}
          onSave={this.handleSaveAndSend}
          onBack={this.handleBack}
          loading={loading}
        />
      </div>
    );
  }
}

export default compose(
  withRouter,
  withStyles(styles),
  withAuthorization(user => !!user),
  withFirebase
)(Application);
